import { Button, MessageModal, ModalProps } from "@blacknut/react-sdk";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppLayout,
  NotificationMessage,
  NotificationMessageKind,
  State,
  notificationService,
  useGame,
  useLayout,
} from "core";
import { useSelector } from "react-redux";
import { useWarioURL } from "src/utils/useWarioURL";
import {
  apiService,
  GamePass,
  RemoteNotificationTrigger,
  Status,
  SupportedOS,
} from "@blacknut/javascript-sdk/dist";
import { useTheme } from "src/theme/ThemeProvider";
import { logD } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import { useSignupURL } from "src/utils/useSignupURL";
import { QRCodeModal } from "../QRCodeModal";
import SwitchToMasterModal from "src/utils/SwitchToMasterModal";
import { PassModal } from "src/utils/useGamePassUnlock/useGamePassUnlock";
import AmazonSubscribeModal from "./AmazonSubscibeModal";
import nativeBridge from "src/utils/NativeBridge";

export interface NotSubscribedProps extends ModalProps {
  isProfileMaster: boolean;
  subscribeURL?: string;
  onClose: () => void;
  onSubscribe: () => void;
  onPlanSubscribed: () => void;
  gamePass?: GamePass;
  redirectUrl: string; // Redirect to this url after switching to Master Profile
  gameId: string;
  isInvalidStatus?: boolean;
  onError: (s: string) => void;
}

export interface TVModalProps extends NotSubscribedProps {
  isUbi: boolean;
}

const TVModal = (props: TVModalProps) => {
  const { t } = useTranslation();
  const { isProfileMaster, subscribeURL, gamePass, isUbi, isInvalidStatus } = props;
  const { organization, subscription } = useSelector(
    (state: State) => state.globalState,
  );
  const subStatus = subscription.mainEntitlement?.subscription?.status;
  const isSuspended = subStatus === Status.SUSPENDED;
  const { createSubURL } = useSignupURL();
  const { create } = useWarioURL();
  const { theme } = useTheme();
  const [URL, setURL] = useState("");

  useEffect(() => {
    const getWarioURL = async () => {
      const warioUrl = await create(organization?.subscriptionUrl, undefined, {
        tokens: isProfileMaster,
      });
      setURL(warioUrl.toString());
    };
    const getSubURL = async () => {
      const subURL = await createSubURL();
      setURL(subURL.toString());
    };

    if (subscribeURL) {
      setURL(subscribeURL);
    } else if (organization?.subscriptionUrl || isInvalidStatus) {
      getWarioURL();
    } else {
      getSubURL();
    }
  }, [
    create,
    createSubURL,
    isInvalidStatus,
    isProfileMaster,
    organization?.subscriptionUrl,
    subscribeURL,
  ]);

  const getDescription = useCallback(() => {
    if (subStatus === Status.PAST_DUE) return t("account.subscriptionStatus.past_dueTV");
    if (subStatus === Status.SUSPENDED && organization?.name)
      return t("account.subscriptionStatus.suspendedTV", {
        organizationName: organization.name,
      });
    if (subStatus === Status.INCOMPLETE)
      return t("account.subscriptionStatus.incompleteTV");

    return t("account.subscriptionStatusDescTV");
  }, [organization?.name, subStatus, t]);

  if (gamePass && !isInvalidStatus) {
    logD(LOGGING_TAG, "Using url: %o", URL);
    return (
      <PassModal
        {...props}
        gamePass={gamePass}
        url={URL}
        title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
        description={
          subStatus === Status.TRIALING
            ? t(
                `subscription.passSubscriptions.subscribe.message${
                  isUbi ? "Ubisoft" : ""
                }TV`,
              )
            : t("dialogs.pass.tvSubscribe.message")
        }
      />
    );
  }

  return (
    <QRCodeModal
      {...props}
      title={t(
        isSuspended
          ? "account.subscriptionStatusSuspendedTitle"
          : "account.subscriptionStatusTitleTV",
      )}
      description={getDescription()}
      btnText={t("dialogs.error.notSubscribed.no")}
      URL={URL}
      theme={theme}
    />
  );
};

const NotSubscribedModal = (props: NotSubscribedProps) => {
  const { t } = useTranslation();
  const {
    isProfileMaster,
    onSubscribe,
    onClose,
    onPlanSubscribed,
    redirectUrl,
    gamePass,
    subscribeURL,
    gameId,
  } = props;
  const isTV = useLayout() === AppLayout.TV;
  const { subscription, organization, config } = useSelector(
    (state: State) => state.globalState,
  );
  const subStatus = subscription.mainEntitlement?.subscription?.status;
  const { game } = useGame({
    gameId: gameId,
  });
  const isUbi = !!game?.addonOption?.isUbisoftProduct;
  const isInvalidStatus =
    !subscription.mainEntitlement ||
    !subscription.mainEntitlement.subscription ||
    subStatus === Status.PAST_DUE ||
    subStatus === Status.SUSPENDED ||
    subStatus === Status.INCOMPLETE;
  const isSuspended = subStatus === Status.SUSPENDED;
  const isTrialing = subStatus === Status.TRIALING;
  const [amzInited, setAmzInited] = useState(false);
  const [amzPaymentAvailable, setAmzPaymentAvailable] = useState(false);

  useEffect(() => {
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload;

      if (notification.trigger === RemoteNotificationTrigger.PLAN_SUBSCRIBED) {
        onPlanSubscribed();
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [onPlanSubscribed]);

  useEffect(() => {
    const init = async () => {
      if (amzInited) return;
      if (
        !gamePass &&
        apiService.userAgent.os === SupportedOS.Fireos &&
        (isInvalidStatus || isTrialing) &&
        nativeBridge.module?.handleInAppSubscribe &&
        nativeBridge.module?.canHandleInAppSubscribe &&
        config?.features?.amazonPayment
      ) {
        const res = await nativeBridge.module.canHandleInAppSubscribe();
        setAmzInited(true);
        setAmzPaymentAvailable(res);
      } else {
        setAmzInited(true);
        setAmzPaymentAvailable(false);
      }
    };
    init();
  }, [
    amzInited,
    config?.features?.amazonPayment,
    gamePass,
    isInvalidStatus,
    isTrialing,
  ]);

  const getMessage = useCallback(() => {
    if (subStatus === Status.PAST_DUE) return t("account.subscriptionStatus.past_due");
    if (subStatus === Status.SUSPENDED && organization?.name)
      return t("account.subscriptionStatus.suspended", {
        organizationName: organization.name,
      });
    if (subStatus === Status.INCOMPLETE)
      return t("account.subscriptionStatus.incomplete");

    return t("account.subscriptionStatusDesc");
  }, [organization?.name, subStatus, t]);

  if (!isProfileMaster) {
    return (
      <SwitchToMasterModal {...props} redirectUrl={redirectUrl} closeModal={onClose} />
    );
  }

  if (!amzInited) {
    return null;
  }

  if (amzPaymentAvailable) {
    return <AmazonSubscribeModal {...props} />;
  }

  if (isTV) {
    return <TVModal {...props} isUbi={isUbi} isInvalidStatus={isInvalidStatus} />;
  }

  if (gamePass && !isInvalidStatus) {
    logD(LOGGING_TAG, "Using subscribeUrl: %o", subscribeURL);
    return (
      <PassModal
        {...props}
        gamePass={gamePass}
        url={subscribeURL || ""}
        title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
        description={
          subStatus === Status.TRIALING
            ? t(
                `subscription.passSubscriptions.subscribe.message${
                  isUbi ? "Ubisoft" : ""
                }`,
              )
            : t("dialogs.pass.subscribe.message")
        }
      />
    );
  }
  return (
    <MessageModal
      {...props}
      title={t(
        isSuspended
          ? "account.subscriptionStatusSuspendedTitle"
          : "account.subscriptionStatusTitle",
      )}
      message={getMessage()}
      onClose={onClose}
      buttons={
        isProfileMaster && [
          <Button variant="secondary" key="cancel" onClick={onClose}>
            {t("dialogs.error.notSubscribed.no")}
          </Button>,
          <Button variant="primary" key="subscribe" onClick={onSubscribe}>
            {t(isSuspended ? "buttons.checkStatus" : "buttons.subscribe")}
          </Button>,
        ]
      }
    />
  );
};

export default NotSubscribedModal;
